import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TooltipProvider } from "../../ui/tooltip";
import { IdentityHeader, RiskBanner } from "./components/IdentityHeader";
import { KpiStrip } from "./components/KpiStrip";
import { AnchorNav } from "./components/AnchorNav";
import { ValueAndPnl } from "./sections/ValueAndPnl";
import { PlayRhythm } from "./sections/PlayRhythm";
import { CasinoDna } from "./sections/CasinoDna";
import { SportsDna } from "./sections/SportsDna";
import { BonusSupportRisk } from "./sections/BonusSupportRisk";
import { EmptyState, WarningNote } from "./components/Primitives";
import { deriveKpis } from "./utils/derive";
import { CurrencyProvider } from "./utils/currency";
import { getPlayerInsights } from "./playerInsightsSlice";

/**
 * Player Insights tab — a 30-second read of one player: who they are, when they
 * play, what they play, how they stake, what they are worth, and whether there
 * is risk.
 *
 * One scrolling dashboard with a sticky in-tab anchor nav; no nested sub-tabs
 * (§2.5). Money is operator-side throughout — GGR, hold, turnover, stake — and
 * all of it is in the one reporting currency UPS chose for this player, which
 * CurrencyProvider hands to every card.
 *
 * The profile comes from UPS (GET /player/{userId}/insights) in the snake_case
 * shape of the design brief's Appendix B. ./data/mockPlayerProfile.js still
 * holds the canonical sample that shape was designed against; it is no longer
 * rendered, but it remains the reference for the payload contract.
 */
export default function PlayerInsights() {
  const dispatch = useDispatch();

  // Same id the sibling tabs use (AccountHistory, BettingHistory), with the
  // localStorage fallback they also keep for a hard reload straight onto the
  // tab, where the redux value has not been seeded yet.
  const userIdFromStore = useSelector((state) => state.playersearch?.userId);
  const userId = userIdFromStore || localStorage.getItem("userId") || "";

  const profile = useSelector((state) => state.playerInsights?.profile);
  const loading = useSelector((state) => state.playerInsights?.loading);
  const error = useSelector((state) => state.playerInsights?.error);
  const loadedFor = useSelector((state) => state.playerInsights?.loadedFor);

  useEffect(() => {
    // Refetch when the operator navigates to a different customer. String
    // compare because the route param arrives as a string and the store may
    // hold either.
    if (userId && String(loadedFor) !== String(userId)) {
      dispatch(getPlayerInsights(userId));
    }
  }, [dispatch, userId, loadedFor]);

  const kpis = useMemo(() => deriveKpis(profile), [profile]);

  if (!userId) {
    return <EmptyState minHeight={220} message="No player selected." />;
  }

  if (error && !loading) {
    return (
      <EmptyState
        minHeight={220}
        message={`${error} Reopen the tab to try again.`}
      />
    );
  }

  // deriveKpis returns null without pnl.totals, and every section already
  // renders its own empty state, so a profile that is still loading or came
  // back thin degrades card-by-card rather than blanking the page.
  const showSkeletons = loading && !profile;

  // Excluded bets mean the money on the page is incomplete, which warrants the
  // amber note; converted bets are complete and just need saying, quietly.
  const currencyNote = profile?.currency_note;
  const moneyIncomplete = (profile?.unconverted_bets || 0) > 0;

  return (
    <CurrencyProvider code={profile?.currency_code} precision={profile?.currency_precision}>
      <TooltipProvider delayDuration={80}>
        {/* tabular-nums once at the root: every figure on the page aligns in its
            column. One restrained load-in; no per-card entrance effects (§5.3).
            Fade only, no slide: a slide would put a transform on this element,
            which is the sticky header's ancestor, and transformed ancestors and
            position:sticky interact badly. */}
        <div className="tabular-nums animate-in fade-in-0 duration-200">
          <IdentityHeader profile={profile || {}} now={new Date()} />

          {profile?.risk_flagged && <RiskBanner />}

          <div className="mt-4">
            <KpiStrip kpis={kpis} loading={showSkeletons} />
          </div>

          {currencyNote &&
            (moneyIncomplete ? (
              <div className="mt-2">
                <WarningNote>{currencyNote}</WarningNote>
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">{currencyNote}</p>
            ))}

          {/* Deliberately NOT wrapped in a spacing div: a sticky element can
              only travel within its own parent's box, so a wrapper that hugs
              the nav would pin it to nothing. Its parent has to be this
              full-height page container, and the margin rides on the nav.
              resetKey re-binds the scroll-spy when a state change rebuilds a
              section's DOM (Section D collapsing, for one). */}
          <AnchorNav className="mt-4" resetKey={loadedFor || "empty"} />

          <div className="mt-6 space-y-8 pb-4">
            <ValueAndPnl profile={profile} kpis={kpis} loading={showSkeletons} />
            <PlayRhythm profile={profile} loading={showSkeletons} />
            <CasinoDna profile={profile} loading={showSkeletons} />
            <SportsDna profile={profile} loading={showSkeletons} />
            <BonusSupportRisk profile={profile} loading={showSkeletons} />
          </div>
        </div>
      </TooltipProvider>
    </CurrencyProvider>
  );
}
